import { useEffect, useRef } from "react";
import { Id, toast } from "react-toastify";

import {
  disableCaptionsTrackingHelper,
  enableCaptionsTrackingHelper,
  getLatestCaptionHelper,
} from "api/api_helpers";
import { createNoCaptionsToast } from "components/shared/toasts";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useUserProperties } from "hooks/use_user_properties";
import { setLatestCaption, setUserEnableLiveCaptions } from "shared/reducers/classroom";
import { RoleType } from "shared/types/user";
import {
  selectActiveRoomIdForCurrentUser,
  selectCaptionerIsPresent,
  selectCurrentUserIsCaptionsLeader,
  selectIsCaptioned,
  selectLiveCaptionResource,
} from "store/selectors/classroom";
import { selectCurrentUserId } from "store/selectors/local";

function CaptionsObserver() {
  const dispatch = useAppDispatch();
  const firstRender = useRef(true);
  const currentUserId = useAppSelector(selectCurrentUserId());
  const isCaptionsLeader = useAppSelector(selectCurrentUserIsCaptionsLeader());
  const currentUserRoomId = useAppSelector(selectActiveRoomIdForCurrentUser());
  const captionsRoomId = useRef("");
  const { enable_live_captions, role } =
    useUserProperties(currentUserId, ["enable_live_captions", "role"]) || {};
  const shouldBeCaptioned = useAppSelector(selectIsCaptioned());
  const captionerIsPresent = useAppSelector(selectCaptionerIsPresent());
  const captionsDoc = useAppSelector(selectLiveCaptionResource());
  const toastIdRef = useRef<Id | null>(null);

  useEffect(() => {
    if (firstRender.current) return;

    function cleanup() {
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
    }

    if (enable_live_captions && !captionerIsPresent) {
      toastIdRef.current = createNoCaptionsToast(shouldBeCaptioned);
    } else {
      cleanup();
    }

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enable_live_captions]);

  useEffect(() => {
    firstRender.current = false;
  }, []);

  useEffect(() => {
    if (role === RoleType.Captioner && !enable_live_captions) {
      dispatch(setUserEnableLiveCaptions({ userId: currentUserId, enableLiveCaptions: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, enable_live_captions]);

  useEffect(() => {
    captionsRoomId.current = currentUserRoomId;
  }, [currentUserRoomId]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    async function startPollingCaptions(resouceUuid: string) {
      await enableCaptionsTrackingHelper(resouceUuid);
      interval = setInterval(async () => {
        const text = await getLatestCaptionHelper(resouceUuid);
        await dispatch(
          setLatestCaption({ text, createdOn: Date.now(), roomId: captionsRoomId.current }),
        );
      }, 3000);
    }
    if (!isCaptionsLeader || !captionsDoc?.external_id) return;
    startPollingCaptions(captionsDoc.external_id);
    return () => {
      clearInterval(interval);
      if (!isCaptionsLeader || !captionsDoc?.external_id) return;
      disableCaptionsTrackingHelper(captionsDoc.external_id);
    };
  }, [dispatch, isCaptionsLeader, captionsDoc?.external_id]);

  return null;
}

export { CaptionsObserver };
